import type { InvoiceLineItem } from 'payments-api/model';
import { useGetInvoicesSuspense } from 'api/payments/generated/fragmentPaymentsAPI';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision } from '@fragment/types/currencyCodes';
import { Icon } from '@fragment/ui/src/components/Icon';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { getCurrencySymbol } from './formatBillingAmount';

type InvoiceRow = {
  id: string;
  status: string;
  amount: React.ReactNode;
  created: string;
};

const emptyValue = {
  Name: 'No Invoices Yet',
  Created: 'N/A',
  Amount: '—',
};

const InvoiceIcon = () => (
  <span className="text-main-500">
    <Icon type="invoice" />
  </span>
);

const computePayinAmount = (lineItems?: InvoiceLineItem[]): JSX.Element => {
  if (!lineItems?.length) return <span>—</span>;
  const payins = lineItems.filter(
    (li) => li.type === InvoiceLineItemType.payin
  );
  if (!payins.length) return <span>—</span>;

  const byCurrency: Record<string, number> = {};
  payins.forEach((li) => {
    byCurrency[li.currency_code] =
      (byCurrency[li.currency_code] ?? 0) + Number(li.amount);
  });

  const parts = Object.entries(byCurrency).map(([currency, totalSmallest]) => {
    const precision = currencyPrecision[currency as CurrencyCode];
    const displayAmount = totalSmallest / 10 ** precision;
    const displayPrecision = Math.min(precision, 6);
    const symbol = getCurrencySymbol(currency);
    return (
      <span key={currency} className="inline-flex items-baseline gap-x-[1ch]">
        <span>{symbol}</span>
        <span>
          {displayAmount.toLocaleString('en-US', {
            minimumFractionDigits: displayPrecision,
            maximumFractionDigits: displayPrecision,
          })}
        </span>
      </span>
    );
  });

  return (
    <>
      {parts.reduce<React.ReactNode[]>(
        (acc, part, i) => (i === 0 ? [part] : [...acc, ', ', part]),
        []
      )}
    </>
  );
};

const schema: ColumnDefinition<InvoiceRow>[] = [
  {
    title: 'Name',
    colspan: 'col-span-3',
    format: (row: InvoiceRow) => (
      <div className="flex items-center gap-x-1">
        <InvoiceIcon />
        <span>{row.id}</span>
      </div>
    ),
  },
  {
    title: 'Created',
    colspan: 'col-span-2',
    format: (row: InvoiceRow) => (
      <span>{new Date(row.created).toLocaleDateString()}</span>
    ),
  },
  {
    title: 'Amount',
    justify: 'right',
    colspan: 'col-span-1',
    format: (row: InvoiceRow) => <span className="ml-auto">{row.amount}</span>,
  },
];

type Props = {
  workspaceId: string;
};

type StatusFilterValue = 'all' | 'draft' | 'active' | 'closed';

const statusFilterOptions = [
  { value: 'all' as const, label: 'All' },
  { value: 'active' as const, label: 'Active' },
  { value: 'closed' as const, label: 'Closed' },
  { value: 'draft' as const, label: 'Draft' },
] as const;

const ColumnHeaders = () => (
  <div className="grid grid-flow-col auto-cols-fr gap-x-[1ch] px-f1 py-f1 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-3">Name</div>
    <div className="col-span-2">Created</div>
    <div className="col-span-1 text-right">Amount</div>
  </div>
);

const SKELETON_MAX_ROWS = 10;

const InvoicesListPageLoading = ({ workspaceId: _workspaceId }: Props) => {
  const [visibleRows, setVisibleRows] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleRows((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_ROWS) {
          directionRef.current = -1;
          return SKELETON_MAX_ROWS;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <PaymentViewLayout
      title="Invoices"
      primaryAction={{
        title: 'New',
        onClick: () => {},
        disabled: true,
      }}
    >
      <div className="flex flex-col w-full">
        <div className="sticky top-0 z-10">
          <TabsV2
            options={statusFilterOptions}
            selectedValue="all"
            onClick={() => {}}
            sort={{ label: 'Recent', order: 'desc', onToggle: () => {} }}
          />
          <ColumnHeaders />
        </div>
        {/* eslint-disable react/no-array-index-key */}
        {Array.from({ length: visibleRows }).map((__, i) => (
          <div
            key={`skeleton-row-${i}`}
            className="h-[40px] border-b-[0.5px] border-main-200"
          />
        ))}
        {/* eslint-enable react/no-array-index-key */}
      </div>
    </PaymentViewLayout>
  );
};

const InvoicesListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Invoices');

  const { data } = useGetInvoicesSuspense();
  const [, setLocation] = useLocation();
  const [selectedStatus, setSelectedStatus] =
    useState<StatusFilterValue>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  const invoices: InvoiceRow[] = useMemo(
    () =>
      data.data.map((invoice) => ({
        id: invoice.id,
        status: invoice.status,
        amount: computePayinAmount(invoice.line_items),
        created: invoice.created,
      })),
    [data]
  );

  const filteredInvoices = useMemo(() => {
    const filtered =
      selectedStatus === 'all'
        ? invoices
        : invoices.filter((invoice) => invoice.status === selectedStatus);
    return [...filtered].sort((a, b) => {
      const diff =
        new Date(b.created).getTime() - new Date(a.created).getTime();
      return sortOrder === 'newest' ? diff : -diff;
    });
  }, [invoices, selectedStatus, sortOrder]);

  const getPath = useCallback(
    (invoice: InvoiceRow) =>
      makePath(['w', workspaceId, 'payments', 'invoices', invoice.id], {
        absolute: true,
      }),
    [workspaceId]
  );

  const handleCreateInvoice = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'create-invoice'], {
        absolute: true,
      })
    );
  }, [workspaceId, setLocation]);

  return (
    <PaymentViewLayout
      title="Invoices"
      primaryAction={{
        title: 'New',
        onClick: handleCreateInvoice,
        'data-testid': 'create-invoice-button',
      }}
    >
      <div className="flex flex-col min-h-f1 w-full h-full">
        <div className="sticky top-0 z-10">
          <TabsV2
            options={statusFilterOptions}
            selectedValue={selectedStatus}
            onClick={setSelectedStatus}
            sort={{
              label: 'Recent',
              order: sortOrder === 'newest' ? 'desc' : 'asc',
              onToggle: () =>
                setSortOrder((prev) =>
                  prev === 'newest' ? 'oldest' : 'newest'
                ),
            }}
          />
          <ColumnHeaders />
        </div>
        <NavTableV2
          textSize="xmd"
          data={filteredInvoices}
          emptyValue={emptyValue}
          getPath={getPath}
          schema={schema}
          showHeader={false}
          stickyHeader={false}
        />
      </div>
    </PaymentViewLayout>
  );
};

export const InvoicesListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<InvoicesListPageLoading workspaceId={workspaceId} />}>
    <InvoicesListPageContent workspaceId={workspaceId} />
  </Suspense>
);
